import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import { Camera, Sparkles, Upload } from "lucide-react";
import {
  CREDIT_COSTS,
  getCurrencyExponent,
  type SupportedCurrencyCode,
} from "@repo/api-schema";
import { mapDraftToFormSeed, type ScanJob } from "@repo/api-hooks";
import { downscaleToJpeg } from "../lib/downscaleImage";
import { useIsTouchDevice } from "../hooks/useIsTouchDevice";
import {
  useCreateUploadTicket,
  useCreateScanJob,
  useMyCredits,
  useScanJob,
} from "../api/hooks";
import type { ExpenseFormInitialDraft } from "./ExpenseForm";

const SCAN_COST = CREDIT_COSTS.SCAN_COST;
const IMAGE_TYPES = "image/jpeg,image/png,image/webp,image/heic";
const PDF_TYPE = "application/pdf";
// The "Upload" picker accepts images and PDF in one OS dialog. "Take photo"
// stays separate: it needs `capture` to force the rear camera, which a combined
// input can't do without also hijacking plain file browsing.
const UPLOAD_TYPES = `${IMAGE_TYPES},${PDF_TYPE}`;

type ScanProgress =
  | { phase: "preparing"; fraction: number; isPdf: boolean }
  | { phase: "uploading"; fraction: number; isPdf: boolean }
  | {
      phase: "queued" | "reading" | "analysing";
      fraction: number;
      isPdf: boolean;
    };

type UploadContentType =
  "image/jpeg" | "image/png" | "image/webp" | "image/heic" | "application/pdf";

/**
 * "Fill from a receipt" bar shown at the top of the New Expense screen. Lets the
 * user snap a photo (touch devices) or upload an image or PDF; runs the OCR +
 * LLM scan; and hands the resulting draft up via `onScanned` so the parent can
 * prefill the form in place (no separate scan page). Web can't earn credits —
 * when out, it points the user to the mobile app. See docs/receipt-scanning.md.
 */
export function ReceiptScanBar({
  group,
  onScanned,
}: {
  group: { id: string; currency: SupportedCurrencyCode };
  onScanned: (draft: ExpenseFormInitialDraft) => void;
}): ReactNode {
  const isTouch = useIsTouchDevice();
  const creditsQ = useMyCredits();
  const createTicket = useCreateUploadTicket();
  const createJob = useCreateScanJob(group.id);
  const [scanJobId, setScanJobId] = useState<string | null>(null);
  const scanJobQ = useScanJob(group.id, scanJobId);
  const refetchCredits = creditsQ.refetch;

  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Historical cached jobs may still contain a refund notice during rollout.
  const [notice, setNotice] = useState<string | null>(null);

  // Two inputs: one camera-capture (Take photo), one plain file picker that
  // takes images *and* PDF (Upload). They stay separate because the capture
  // input forces the rear camera, which would also hijack plain file browsing
  // if merged into one input.
  const cameraRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const balance = creditsQ.data?.balance ?? 0;
  const canScan = balance >= SCAN_COST && progress === null;
  const displayedProgress = progressForJob(progress, scanJobQ.data);

  const applyResult = useCallback(
    (result: NonNullable<ScanJob["result"]>) => {
      if (result.refundReason !== undefined) {
        setNotice(
          result.refundReason === "EMPTY_DRAFT"
            ? "We couldn't read anything off that receipt, so you haven't been charged a credit. Try a clearer photo, or fill the expense in below."
            : "That scan wasn't charged a credit.",
        );
      }
      const currency = (result.draft.currency ??
        group.currency) as SupportedCurrencyCode;
      const exponent = getCurrencyExponent(currency);
      const seed = mapDraftToFormSeed({
        draft: result.draft,
        groupCurrency: currency,
        participantIds: [],
      });
      onScanned({
        label: seed.label,
        currency: seed.currency,
        items: seed.items.map((it) => ({
          label: it.label,
          amount: (it.cost / 10 ** exponent).toFixed(exponent),
          ...(it.splitType ? { splitType: it.splitType } : {}),
          ...(it.itemIndexes ? { itemIndexes: it.itemIndexes } : {}),
        })),
        lowConfidenceFields: seed.lowConfidenceFields,
        receiptTotal: seed.total,
        reviewNotes: seed.reviewNotes,
        translation: result.draft.translation,
      });
      setScanJobId(null);
      setProgress(null);
      void refetchCredits();
    },
    [group.currency, onScanned, refetchCredits],
  );

  useEffect(() => {
    const job = scanJobQ.data;
    if (!job || !scanJobId) return;
    const id = window.setTimeout(() => {
      if (job.stage === "COMPLETE" && job.result) {
        applyResult(job.result);
        return;
      }
      if (job.stage === "FAILED") {
        setError(
          job.error ??
            "Couldn't read that receipt. This attempt used one credit; try a clearer image or enter it manually.",
        );
        setScanJobId(null);
        setProgress(null);
        void refetchCredits();
        return;
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [applyResult, refetchCredits, scanJobId, scanJobQ.data]);

  async function onFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file || !canScan) return;
    await runScan(file);
  }

  async function runScan(file: File): Promise<void> {
    setError(null);
    setNotice(null);
    const isPdf = file.type === PDF_TYPE;
    let submitted = false;
    setProgress({ phase: "preparing", fraction: 0.03, isPdf });
    try {
      // Images are downscaled + re-encoded to JPEG so a multi-MB camera photo
      // fits the upload cap; a PDF is uploaded as-is (downscaling would corrupt
      // it, and Textract handles its pages asynchronously).
      const blob = isPdf ? file : await downscaleToJpeg(file);
      const contentType: UploadContentType = isPdf
        ? PDF_TYPE
        : normaliseImageType(blob.type);
      setProgress({ phase: "preparing", fraction: 0.08, isPdf });
      const contentHash = await sha256Hex(blob);

      const ticket = await createTicket.mutateAsync({
        contentType,
        byteSize: blob.size,
        contentHash,
      });
      if (ticket.uploadRequired) {
        if (!ticket.uploadUrl) throw new Error("Upload ticket is incomplete");
        setProgress({ phase: "uploading", fraction: 0.1, isPdf });
        await uploadWithProgress(
          ticket.uploadUrl,
          blob,
          contentType,
          ticket.checksumSha256,
          (fraction) =>
            setProgress({
              phase: "uploading",
              fraction: 0.1 + fraction * 0.25,
              isPdf,
            }),
        );
      }

      setProgress({ phase: "queued", fraction: 0.35, isPdf });
      submitted = true;
      const job = await createJob.mutateAsync({
        objectKey: ticket.objectKey,
        contentHash,
        attemptId: ticket.attemptId,
      });
      if (job.stage === "COMPLETE" && job.result) {
        applyResult(job.result);
      } else if (job.stage === "FAILED") {
        throw new Error(job.error ?? "Scan failed");
      } else {
        setScanJobId(job.id);
      }
    } catch (err) {
      setError(scanSubmissionError(err, submitted));
      setProgress(null);
    }
  }

  return (
    <div className="space-y-3 rounded-md border border-outline-variant/60 bg-surface-container-low p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-label-lg text-on-surface">Fill from a receipt</p>
        <span className="inline-flex items-center gap-1.5 text-label-sm text-on-surface-variant">
          <Sparkles className="size-4" />
          {creditsQ.isLoading ? "Checking credits…" : `${balance} credits`}
          <span aria-hidden="true">·</span>
          {SCAN_COST} per attempt
        </span>
      </div>

      <input
        ref={cameraRef}
        type="file"
        accept={IMAGE_TYPES}
        capture="environment"
        className="hidden"
        onChange={onFile}
      />
      <input
        ref={uploadRef}
        type="file"
        accept={UPLOAD_TYPES}
        className="hidden"
        onChange={onFile}
      />

      {balance < SCAN_COST ? (
        <p className="rounded-md bg-surface-container px-3 py-2 text-body-sm text-on-surface-variant">
          You're out of credits. Earn more by watching a short video in the
          Tailspend mobile app, then come back to scan here.
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-body-sm text-error">
          {error}
        </p>
      ) : null}

      {notice ? (
        <p
          role="status"
          className="rounded-md bg-surface-container px-3 py-2 text-body-sm text-on-surface-variant"
        >
          {notice}
        </p>
      ) : null}

      {displayedProgress ? (
        <ScanningStatus
          key={displayedProgress.phase}
          progress={displayedProgress}
        />
      ) : (
        <div className={`grid gap-2 ${isTouch ? "grid-cols-2" : ""}`}>
          {isTouch ? (
            <ScanTile
              icon={<Camera className="size-5" />}
              label="Take photo"
              disabled={!canScan}
              onClick={() => cameraRef.current?.click()}
            />
          ) : null}
          <ScanTile
            icon={<Upload className="size-5" />}
            label="Upload image or PDF"
            disabled={!canScan}
            onClick={() => uploadRef.current?.click()}
          />
        </div>
      )}
    </div>
  );
}

function scanSubmissionError(error: unknown, submitted: boolean): string {
  const message = apiErrorMessage(error);
  if (message.includes("Daily scan limit")) {
    return "You've reached today's receipt scan limit. Try again after 00:00 UTC.";
  }
  if (message.includes("Insufficient credits")) {
    return "You need one credit to scan a receipt.";
  }
  return submitted
    ? "Couldn't read that receipt. If the attempt reached us, it used one credit; try a clearer image or enter it manually."
    : "Couldn't upload that receipt. No credit was used; please try again.";
}

function apiErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error !== "object" || error === null || !("error" in error)) {
    return "";
  }
  const detail = (error as { error?: { message?: unknown } }).error;
  return typeof detail?.message === "string" ? detail.message : "";
}

/** A single capture option in the scan bar. */
function ScanTile({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  disabled: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-outline-variant/60 bg-surface-container-lowest px-3 py-4 text-label-md text-on-surface transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
    >
      <span className="text-primary">{icon}</span>
      {label}
    </button>
  );
}

/**
 * Inline status backed by real client-side phases. The final server request is
 * deliberately indeterminate: the synchronous API cannot expose whether OCR
 * or structuring is active, so we do not show fabricated percentage progress.
 */
function ScanningStatus({ progress }: { progress: ScanProgress }): ReactNode {
  const reduceMotion = useReducedMotion();
  const [takingLonger, setTakingLonger] = useState(false);

  useEffect(() => {
    if (progress.phase === "preparing" || progress.phase === "uploading")
      return;
    const id = window.setTimeout(() => setTakingLonger(true), 8_000);
    return () => window.clearTimeout(id);
  }, [progress.phase]);

  const label =
    progress.phase === "preparing"
      ? "Preparing and checking your receipt…"
      : progress.phase === "uploading"
        ? "Uploading receipt…"
        : progress.phase === "queued"
          ? "Scan queued…"
          : progress.phase === "reading"
            ? "Reading text from the receipt…"
            : "Analysing items and totals…";

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center gap-3 py-6 text-center"
    >
      <motion.p
        key={progress.phase}
        initial={reduceMotion ? false : { opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-body-sm text-on-surface-variant"
      >
        {label}
      </motion.p>
      <div
        role="progressbar"
        aria-label="Receipt scan"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress.fraction * 100)}
        className="h-1.5 w-full max-w-64 overflow-hidden rounded-full bg-surface-container-high"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500"
          style={{ width: `${progress.fraction * 100}%` }}
        />
      </div>
      {takingLonger ? (
        <p className="text-label-sm text-on-surface-variant">
          {progress.isPdf
            ? "Multi-page PDFs can take a little longer."
            : "Still working — detailed receipts can take a little longer."}
        </p>
      ) : null}
    </div>
  );
}

/** PUT a receipt to its presigned URL while reporting actual bytes uploaded. */
function uploadWithProgress(
  url: string,
  body: Blob,
  contentType: UploadContentType,
  checksumSha256: string | undefined,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    request.setRequestHeader("content-type", contentType);
    if (checksumSha256) {
      request.setRequestHeader("x-amz-checksum-sha256", checksumSha256);
    }
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(1, event.loaded / event.total));
      }
    });
    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(1);
        resolve();
      } else {
        reject(new Error(`Upload failed (${request.status})`));
      }
    });
    request.addEventListener("error", () => reject(new Error("Upload failed")));
    request.send(body);
  });
}

async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await blob.arrayBuffer(),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function progressForJob(
  local: ScanProgress | null,
  job: ScanJob | undefined,
): ScanProgress | null {
  if (!local || !job || job.stage === "COMPLETE" || job.stage === "FAILED") {
    return local;
  }
  return {
    phase:
      job.stage === "READING_TEXT"
        ? "reading"
        : job.stage === "ANALYSING"
          ? "analysing"
          : "queued",
    fraction: job.progress / 100,
    isPdf: local.isPdf,
  };
}

/** Map a browser image File MIME to one of the contract's accepted types. */
function normaliseImageType(
  mime: string,
): Exclude<UploadContentType, "application/pdf"> {
  switch (mime) {
    case "image/jpeg":
    case "image/png":
    case "image/webp":
    case "image/heic":
      return mime;
    default:
      return "image/jpeg";
  }
}
